import { useCallback, useState } from 'react'
import { useForm, Controller, FormProvider, get } from 'react-hook-form'
import { useNavigate } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import PropTypes from 'prop-types'
import {
  Modal as AntModal,
  Button,
  DatePicker,
  Input,
  Form as AntForm,
  Col,
  Divider,
  notification
} from 'antd'
import produce from 'immer'
import moment from 'moment'

import * as ROUTES from 'admin/routes'
import { useApi } from 'admin/lib/Api'
import { queryKeys } from 'admin/lib/Api/queries-common'

export function EditAccount({ account, isNew, accountId }) {
  const navigate = useNavigate()
  const [submitting, setSubmitting] = useState(false)
  const queryClient = useQueryClient()

  //
  // Form setup & values

  const draftItem = isNew ? blankAccount : account

  const form = useForm({
    defaultValues: draftItem,
    mode: 'all',
    criteriaMode: 'firstError'
  })

  //
  // Callbacks

  const api = useApi()
  const onSubmit = useCallback(
    async data => {
      setSubmitting(true)

      const submissionData = produce(draftItem, draft => {
        draft.name = data.name
        draft.companyName = data.companyName
        draft.renewalDate = data.renewalDate
      })

      const [, status, error] = isNew
        ? await api.registerAccount(
            data.name,
            data.companyName,
            data.renewalDate
          )
        : await api.updateAccount(submissionData)

      if (error) {
        AntModal.error({
          title: 'Error Saving Account',
          content: (
            <>
              <p>Error, you may wish to try again. </p>
              <p>{`Details: ${status}: ${error}`}</p>
            </>
          )
        })
      } else {
        setSubmitting(false)
        queryClient.invalidateQueries(queryKeys.userSelfAccountMappings)
        navigate(ROUTES.ACCOUNTS.LIST)
        notification.success({
          message: 'Successfully saved account'
        })
      }
    },
    [api, draftItem, isNew, navigate, queryClient]
  )

  return (
    <AntForm layout="vertical" onFinish={form.handleSubmit(onSubmit)}>
      <FormProvider {...form}>
        <Controller
          name="name"
          rules={{
            required: true
          }}
          render={({ field }) => (
            <AntForm.Item
              label="Account Name [eg. TSmail Ltd - UK account]"
              {...getErrorProps(field.name, form.errors)}
            >
              <Input {...field} />
            </AntForm.Item>
          )}
        />

        <Controller
          name="companyName"
          render={({ field }) => (
            <AntForm.Item label="Company Name [eg. TSmail Ltd]">
              <Input {...field} />
            </AntForm.Item>
          )}
        />

        <Controller
          name="renewalDate"
          render={({ field }) => (
            <AntForm.Item label="Renewal Date">
              <DatePicker
                value={field.value ? moment(field.value) : null}
                onChange={date => field.onChange(date ? date.toDate() : null)}
              />
            </AntForm.Item>
          )}
        />

        <Divider type="horizontal" />

        <Col align="middle" style={{ marginBottom: '5rem' }}>
          <Button type="primary" htmlType="submit" disabled={submitting}>
            {isNew ? 'Create' : 'Update'}
          </Button>
        </Col>
      </FormProvider>
    </AntForm>
  )
}

const blankAccount = {
  name: '',
  companyName: '',
  renewalDate: null
}

EditAccount.propTypes = {
  account: PropTypes.object,
  isNew: PropTypes.bool.isRequired
}

function getErrorProps(name, errors) {
  const error = get(errors, name, null)

  if (error) {
    return {
      hasFeedback: true,
      validateStatus: 'error'
    }
  } else {
    return {}
  }
}
